import { useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { v4 as uuidv4 } from 'uuid'

import { ChatAddDrivers } from '@/components/Chat/ChatAddDrivers'
import { ChatAddEquipments } from '@/components/Chat/ChatAddEquipments'
import { ConnectTerminalTelematics } from '@/components/ConnectTerminalTelematics'
import { allCarriersDeclined, AllDeclinedOverlay } from '@/components/quotes/AllDeclinedOverlay'
import {
  ApplicationSummarySection,
  type MissingDataMessage,
  SUMMARY_SECTION_PARAM,
  type SummarySectionKey,
} from '@/components/quotes/ApplicationSummarySection'
import { applicationToPackage } from '@/components/quotes/applicationToPackage'
import {
  applicationToQuoteCards,
  indicationStages,
  indicationsToOfferCards,
} from '@/components/quotes/applicationToQuoteCards'
import { CarrierAttestationDialog } from '@/components/quotes/CarrierAttestationDialog'
import { channelPickCartKeys, channelPickMessage } from '@/components/quotes/channelPick'
import {
  type ClaimsCapabilityFilterKey,
  FiltersSection,
  type QuoteSortBy,
} from '@/components/quotes/FiltersSection'
import { PackageAnnouncementDialog } from '@/components/quotes/PackageAnnouncementDialog'
import { PackageCard } from '@/components/quotes/PackageCard'
import { QuoteCardsCarousel, type QuoteCarouselHandle } from '@/components/quotes/QuoteCardsCarousel'
import {
  type CoiSpeed,
  isQuoteExpired,
  type QuoteCardData,
  QuoteCardStage,
} from '@/components/quotes/quoteCardTypes'
import { QuoteCartCheckout } from '@/components/quotes/QuoteCartCheckout'
import { useQuoteCart } from '@/components/quotes/QuoteCartContext'
import { cardCartKeys } from '@/components/quotes/quoteCartItems'
import { filterAndSortQuotes } from '@/components/quotes/quoteFilters'
import {
  quoteListProperties,
  QuotesAnalyticsProvider,
  QuotesEvent,
  QuotesSurface,
  useQuotesPageViewed,
  useQuotesStateShown,
  useTrackQuotesEvent,
} from '@/components/quotes/quotesAnalytics'
import { QuotesPageSkeleton } from '@/components/quotes/QuotesPageSkeleton'
import { AdditionalInfoSummary } from '@/components/quotes/summary/AdditionalInfoSummary'
import { BusinessDetailsSummary } from '@/components/quotes/summary/BusinessDetailsSummary'
import { ClaimsHistorySummary } from '@/components/quotes/summary/ClaimsHistorySummary'
import {
  CoverageNeedsSummary,
  // Aliased: `selectedCoverages` here is the coverage *filter* the customer is holding.
  selectedCoverages as coveragesRequestedOnApplication,
} from '@/components/quotes/summary/CoverageNeedsSummary'
import { MyDocumentsSummary } from '@/components/quotes/summary/MyDocumentsSummary'
import { RequoteOnSaveProvider } from '@/components/quotes/summary/requoteOnSave'
import { TopQuotesHeader } from '@/components/quotes/TopQuotesHeader'
import { UploadCarrierDocumentDialog } from '@/components/quotes/UploadCarrierDocumentDialog'
import { UploadQuoteCard } from '@/components/quotes/UploadQuoteCard'
import { usePackageAnnouncement } from '@/components/quotes/usePackageAnnouncement'
import { useQuoteArrivalNotifications } from '@/components/quotes/useQuoteArrivalNotifications'
import { useQuotesCascade } from '@/components/quotes/useQuotesCascade'
import { Button } from '@/components/ui/button'
import { NotificationsPrompt } from '@/components/ui/NotificationsPrompt'
import { useInView } from '@/hooks/useInView'
import { CacheKeys } from '@/lib/cache'
import type { DomEvidence } from '@/lib/checkoutEvidence'
import { isAttestationRequiredError, useCarrierAttestations } from '@/models/attestation'
import { useOrgBroker } from '@/models/broker'
import { useDeleteCustomerQuote } from '@/models/customerQuote'
import { DocumentType } from '@/models/document'
import { useTrailers, useTrucks } from '@/models/equipment'
import { type InsuranceApplication, useIndicationCheckout } from '@/models/insuranceApplication'
import { InsuranceIndicationStatus } from '@/models/insuranceTypes'
import {
  collectLossRunClaims,
  countLossRunClaims,
  hasOutstandingLossRunRequest,
  type LossRunClaim,
  type LossRunDocument,
  type LossRunRequest,
  LossRunRequestStatus,
  useOrgLossRunRequests,
} from '@/models/lossRunRequest'
import { isFleetReadonly, type Org, OrgStatus, useCurrentUser } from '@/models/user'
import { NavCartButton } from '@/routes/Public/marketing/components/NavCartButton'
import { BlurredUntilReviewed } from './BlurredUntilReviewed'
import { ReviewAttestationCard } from './ReviewAttestationCard'
import { useQuotesSeen } from './TimeToQuotingLayout'

interface InsuranceQuotesPageProps {
  application: InsuranceApplication
  /** Refetches the application after an edit dialog saves, so the summary reflects it. */
  onEditSuccess: () => Promise<unknown> | void
  /** Forces read-only regardless of the fleet's status, keeping the cart and checkout. */
  readonly?: boolean
  /** Puts the cart opener in the summary row — for layouts whose nav has no room for one. */
  showCartButton?: boolean
  /** Drops the all-declined overlay: staff open this board to read the declines it covers. */
  staffView?: boolean
  /** The application's own org — staff views pass the customer's, not the signed-in staffer's. */
  org?: Org
}

/**
 * The Terminal link is the real "connect provider" flow; the summary popover renders
 * whatever we hand it in place of its placeholder button. Defined at module scope so
 * it isn't remounted on every render of the page.
 */
function TerminalConnectButton({ section, onConnected }: { section: string; onConnected: () => void }) {
  const track = useTrackQuotesEvent()

  return (
    <ConnectTerminalTelematics
      handleSuccess={onConnected}
      onOpen={() => track(QuotesEvent.SUMMARY_CONNECT_PROVIDER_CLICKED, { section })}
      buttonProps={{ size: 'sm', children: 'Connect provider' }}
    />
  )
}

/**
 * What the claims-history card is short of. Pending has to mean a request is actually open —
 * none exist before the BOR is signed, and the card was telling those customers a carrier
 * owed us a loss run nobody had asked for.
 */
function claimsMissingDataMessage(
  requests: LossRunRequest[],
  documents: LossRunDocument[],
  claims: LossRunClaim[]
): MissingDataMessage | undefined {
  if (claims.length > 0) return undefined
  if (hasOutstandingLossRunRequest(requests)) return 'Loss runs pending'
  // A document with no request behind it is an upload the parser read nothing out of.
  return requests.length === 0 && documents.length === 0 ? 'Information needed' : undefined
}

/**
 * The quotes experience, shown once carriers have been asked for prices. The conversation
 * has ended by this point: no chat step is injected here, so anything still outstanding
 * (loss runs, BOR) is surfaced by the page on its own terms.
 */
// The card row follows the summary, filters and Top Quotes heading in the entrance cascade.
const CARDS_STEP = 3

function cascadeStep(step: number): CSSProperties {
  return { '--cascade-step': step } as CSSProperties
}

export function InsuranceQuotesPage(props: InsuranceQuotesPageProps) {
  return (
    <QuotesAnalyticsProvider
      surface={QuotesSurface.APP}
      applicationId={props.application.id}
      orgId={props.application.orgId}
    >
      <InsuranceQuotesPageBody {...props} />
    </QuotesAnalyticsProvider>
  )
}

function InsuranceQuotesPageBody({
  application,
  onEditSuccess,
  readonly,
  showCartButton,
  staffView,
  org: orgProp,
}: InsuranceQuotesPageProps) {
  const track = useTrackQuotesEvent()
  const [sortBy, setSortBy] = useState<QuoteSortBy>('best')
  const [selectedRatings, setSelectedRatings] = useState<Set<string>>(new Set())
  const [selectedCoiSpeeds, setSelectedCoiSpeeds] = useState<Set<CoiSpeed>>(new Set())
  const [selectedCoverages, setSelectedCoverages] = useState<Set<string>>(new Set())
  const [financingAvailable, setFinancingAvailable] = useState(false)
  const [selectedClaimsCapabilities, setSelectedClaimsCapabilities] = useState<
    Set<ClaimsCapabilityFilterKey>
  >(new Set())
  const carouselRef = useRef<QuoteCarouselHandle>(null)
  const boardRef = useRef<HTMLDivElement>(null)
  // A customer reading their application above the board, or scrolled past it, hasn't seen its prices yet.
  const boardInView = useInView(boardRef)
  // A roster page hands the section over in the URL; an unknown one simply opens nothing.
  const [searchParams] = useSearchParams()
  const openSection = (searchParams.get(SUMMARY_SECTION_PARAM) as SummarySectionKey | null) ?? undefined
  const cart = useQuoteCart()
  const queryClient = useQueryClient()
  const { mutateAsync: checkout } = useIndicationCheckout(application.id, onEditSuccess)
  // The arrows live in the header, above the scroller they drive, so the carousel
  // reports where it is rather than each of them working it out.
  const [edges, setEdges] = useState({ canScrollPrev: false, canScrollNext: true })
  // A checkout refused for want of the confirmation shows the step now; the refetch it triggers follows.
  const [checkoutNeedsReview, setCheckoutNeedsReview] = useState(false)
  // Under the review card the whole page starts blurred; the customer can lift it off their information.
  const [informationShown, setInformationShown] = useState(false)
  const [cartNotice, setCartNotice] = useState<string | null>(null)
  // Staff read the board as it is; only the customer reviews what was filled in, before any price.
  const reviewing = !staffView && (application.needsReviewAttestation || checkoutNeedsReview)

  const { trucks, isLoading: isLoadingTrucks } = useTrucks({
    orgId: application.orgId,
    shouldFetchAllPages: true,
  })
  const { trailers, isLoading: isLoadingTrailers } = useTrailers({
    orgId: application.orgId,
    shouldFetchAllPages: true,
  })
  const {
    lossRunRequests,
    documents: lossRunDocuments,
    isParsing: claimsParsing,
    isLoading: isLoadingLossRuns,
  } = useOrgLossRunRequests(application.orgId)
  const { broker, isLoading: isLoadingBroker } = useOrgBroker(orgProp?.id)
  // Reads the already-cached user (the route blocks on it before mounting this page) for the
  // org-level bits the application payload doesn't carry.
  const { user, refetch: refetchUser } = useCurrentUser({ enabled: false })
  const org = orgProp ?? user?.currentOrg
  const isReadonly = readonly || isFleetReadonly(user)
  const underwriting = org?.status === OrgStatus.UNDERWRITING
  // Underwriting locks the application, not the files: an underwriter can still ask for one.
  const documentsReadonly = readonly || (isFleetReadonly(user) && !underwriting)
  const orgApplication = org?.currentInsuranceApplication
  const travelDistanceOptions = orgApplication?.travelDistanceOptions ?? []

  // Org edits land on the org payload, application edits on the application — refetch both.
  // Staff read the org through its own query, so the user refetch alone leaves them stale.
  function onSaved() {
    onEditSuccess()
    refetchUser()
    void queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS, application.orgId] })
  }

  const deleteQuote = useDeleteCustomerQuote(application.orgId)
  const [attestationCardId, setAttestationCardId] = useState<string | null>(null)
  // Which card raised the prompt, and which of its documents — one card can ask for two.
  const [uploadTarget, setUploadTarget] = useState<{ id: string; documentType: DocumentType } | null>(null)
  // A quote the customer just uploaded, held until its card is on the board to scroll to.
  const [uploadedQuoteId, setUploadedQuoteId] = useState<string | null>(null)

  const { templates: attestationTemplates, isLoading: isLoadingAttestations } = useCarrierAttestations(
    application.orgId
  )
  const carrierTermsAvailable = attestationTemplates.length > 0

  // Only what a broker actually flagged is badged — the page never picks one itself.
  const quotes = useMemo(
    () => applicationToQuoteCards(application.indications, broker?.name, carrierTermsAvailable),
    [application.indications, broker?.name, carrierTermsAvailable]
  )

  const quotePackage = useMemo(
    () =>
      application.package
        ? applicationToPackage(
            application.package,
            indicationsToOfferCards(application.indications, carrierTermsAvailable),
            quotes
          )
        : null,
    [application.package, application.indications, carrierTermsAvailable, quotes]
  )
  // Customers only: staff building the package don't need telling it exists.
  const announcement = usePackageAnnouncement(staffView ? undefined : quotePackage?.id)
  const trackNotificationOpened = useCallback(() => track(QuotesEvent.NOTIFICATION_OPENED), [track])
  useQuoteArrivalNotifications(quotes, {
    applicationId: application.id,
    enabled: !staffView,
    onOpened: trackNotificationOpened,
  })

  // What the customer actually asked to be covered for — "Best" ranks the carriers that
  // priced all of it above the ones that only came back on part of it.
  const requestedCoverages = useMemo(
    () => new Set(coveragesRequestedOnApplication(application).map((coverage) => coverage.type)),
    [application]
  )

  const claims = collectLossRunClaims(lossRunRequests)
  // COMPLETED is stamped after the document is stored and parsed, so a completed request
  // with no claims is a clean record rather than one still being read. Every request has
  // to be in: one insurer reporting clean says nothing about the one still outstanding.
  const claimsAreIn =
    lossRunRequests.length > 0 &&
    lossRunRequests.every((request) => request.status === LossRunRequestStatus.COMPLETED)

  // By every member, not just the card id: a grouped card's other lines would lose their name.
  const cardsByIndicationId = useMemo(() => {
    const byId = new Map<string, QuoteCardData>()
    for (const quote of quotes) {
      ;(quote.memberIds ?? [quote.id]).forEach((id) => byId.set(id, quote))
    }
    return byId
  }, [quotes])

  // A persisted cart outlives the indications it names: a requote replaces them, and a key for
  // a gone indication stays counted in the nav and would be sent at checkout though the sidebar,
  // which lists only what the page still prices, never showed it. An expired quote can't be
  // bought, so it goes too.
  const liveCartKeys = useMemo(() => {
    const keys = new Set<string>()
    for (const quote of quotes) {
      if (quote.stage === QuoteCardStage.PRICED && !isQuoteExpired(quote))
        cardCartKeys(quote).forEach((key) => keys.add(key))
    }
    return keys
  }, [quotes])

  const { keys: cartKeys, remove: removeFromCart } = cart
  useEffect(() => {
    if (liveCartKeys.size === 0) return
    const stale = [...cartKeys].filter((key) => !liveCartKeys.has(key))
    if (stale.length > 0) removeFromCart(stale)
  }, [liveCartKeys, cartKeys, removeFromCart])

  // The evidence is what the customer had on screen at the click — Corgi keeps it against the bind.
  async function applySelectedIndications(indicationIds: string[], evidence: DomEvidence) {
    const carrierNames = [
      ...new Set(
        indicationIds.map((id) => cardsByIndicationId.get(id)?.carrierName).filter((name) => !!name)
      ),
    ]
    try {
      await checkout({
        indicationIds,
        clientId: uuidv4(),
        content: { component: 'P', content: carrierNames.join('\n') },
        ...evidence,
      })
    } catch (error) {
      if (!staffView && isAttestationRequiredError(error)) {
        cart.close()
        setCheckoutNeedsReview(true)
      }
      throw error
    }
    setCartNotice(null)
  }

  // The quotes wait for the refetched application; a quote picked by reply is then waiting in the cart.
  async function confirmReview() {
    const pick = application.pendingChannelPick
    await onEditSuccess()
    setCheckoutNeedsReview(false)
    if (!pick) return
    const pickedKeys = channelPickCartKeys(pick, quotes)
    if (pickedKeys.length === 0) return
    cart.add(pickedKeys)
    setCartNotice(channelPickMessage(pick))
    cart.open()
  }

  // Ranking one price against itself says nothing, so the sort waits for a second.
  const pricedQuoteCount = quotes.filter((quote) => quote.stage === QuoteCardStage.PRICED).length
  const noCarrierPriced = allCarriersDeclined(
    indicationStages(application.indications),
    application.awaitingCarrierCount
  )
  // Withheld declines empty the board too, so this isn't simply "no quotes".
  const showsEmptyState = quotes.length === 0 && application.awaitingCarrierCount === 0 && !noCarrierPriced
  // The awaiting count is only Rocklake's cards whose rows haven't landed; every other carrier waits as PENDING.
  const carrierStillPricing =
    application.awaitingCarrierCount > 0 ||
    application.indications.some((indication) => indication.status === InsuranceIndicationStatus.PENDING)
  const policyExpirationDate = application.metadata?.currentInsurance?.expirationDate ?? undefined

  useQuotesPageViewed(reviewing ? undefined : application.id, {
    ...quoteListProperties(quotes),
    awaiting_carrier_count: application.awaitingCarrierCount,
    requested_coverages: [...requestedCoverages],
    is_readonly: isReadonly,
    is_empty: showsEmptyState,
    all_carriers_declined: noCarrierPriced,
    has_policy_expiration_date: !!policyExpirationDate,
  })
  useQuotesStateShown(QuotesEvent.EMPTY_STATE_SHOWN, showsEmptyState && !reviewing)

  const visibleQuotes = useMemo(
    () =>
      filterAndSortQuotes(quotes, {
        sortBy,
        selectedRatings,
        selectedCoiSpeeds,
        selectedCoverages,
        financingAvailable,
        selectedClaimsCapabilities,
        requestedCoverages,
      }),
    [
      quotes,
      sortBy,
      selectedRatings,
      selectedCoiSpeeds,
      selectedCoverages,
      financingAvailable,
      selectedClaimsCapabilities,
      requestedCoverages,
    ]
  )
  const boardReady = !isLoadingBroker && !isLoadingAttestations
  const cascade = useQuotesCascade(boardReady)
  // A carrier's price the filters let through ends the customer's time to quoting; their own upload doesn't.
  // Only once the cards have cascaded in: under the splash or mid-entrance they're in view but not visible.
  useQuotesSeen(
    !reviewing &&
      cascade === 'done' &&
      boardInView &&
      visibleQuotes.some((quote) => quote.stage === QuoteCardStage.PRICED && !quote.isCustomerUploaded)
  )

  // An uploaded quote sorts behind everything we priced, so it lands off-screen to the right.
  useEffect(() => {
    if (!uploadedQuoteId) return
    const card = visibleQuotes.find((quote) => (quote.memberIds ?? [quote.id]).includes(uploadedQuoteId))
    if (!card) return
    setUploadedQuoteId(null)
    carouselRef.current?.scrollToQuote(card.id)
  }, [uploadedQuoteId, visibleQuotes])

  // Changes when the sort or any filter changes — resets the carousel to the first card.
  const orderKey = useMemo(
    () =>
      JSON.stringify([
        sortBy,
        [...selectedRatings].sort(),
        [...selectedCoiSpeeds].sort(),
        [...selectedCoverages].sort(),
        financingAvailable,
        [...selectedClaimsCapabilities].sort(),
      ]),
    [
      sortBy,
      selectedRatings,
      selectedCoiSpeeds,
      selectedCoverages,
      financingAvailable,
      selectedClaimsCapabilities,
    ]
  )

  function toggleIn<T>(setter: React.Dispatch<React.SetStateAction<Set<T>>>, value: T) {
    setter((current) => {
      const next = new Set(current)
      if (next.has(value)) next.delete(value)
      else next.add(value)
      return next
    })
  }

  function clearAllFilters() {
    setSelectedRatings(new Set())
    setSelectedCoiSpeeds(new Set())
    setSelectedCoverages(new Set())
    setFinancingAvailable(false)
    setSelectedClaimsCapabilities(new Set())
  }

  // One Terminal link connects the customer's ELD/telematics provider, so a single
  // connected record names the provider on both the drivers and trucks cards.
  const connectedProvider = application.telematics.find((t) => t.providerType)?.providerType
  const providerLabel = connectedProvider
    ? connectedProvider.charAt(0).toUpperCase() + connectedProvider.slice(1)
    : undefined

  // Counts stay undefined until loaded — the hooks default to [], and showing "Trucks 0"
  // before the fetch lands reads as "you have no trucks" rather than "still loading".
  const summaryData = {
    driverCount: application.business?.drivers.length,
    truckCount: isLoadingTrucks ? undefined : trucks.length,
    trailerCount: isLoadingTrailers ? undefined : trailers.length,
    fleetCountsLoading: isLoadingTrucks || isLoadingTrailers,
    eldConnectedTo: providerLabel,
    telematicsConnectedTo: providerLabel,
    showClaimsHistory: true,
    // No count until the claims are actually in: a loss run can be on file for a minute or
    // two before they come out of it, and "0" there would read as a clean record.
    claimsCount: claims.length > 0 || claimsAreIn ? countLossRunClaims(claims) : undefined,
    // Undefined while loading, like the counts above: an empty fetch is not an answer.
    claimsMissingData: isLoadingLossRuns
      ? undefined
      : claimsMissingDataMessage(lossRunRequests, lossRunDocuments, claims),
    claimsParsing,
    connectActions: {
      drivers: <TerminalConnectButton section="drivers" onConnected={onSaved} />,
      trucks: <TerminalConnectButton section="trucks" onConnected={onSaved} />,
    },
    content: {
      'coverage-needs': (
        <CoverageNeedsSummary application={application} onSuccess={onSaved} readonly={isReadonly} />
      ),
      'business-details': (
        <BusinessDetailsSummary
          application={application}
          business={application.business}
          onSuccess={onSaved}
          readonly={isReadonly}
        />
      ),
      drivers: (
        <ChatAddDrivers
          edit
          slim
          readonly={isReadonly}
          orgId={application.orgId}
          renderFooter={(open) => (
            <Button type="button" variant="outline" size="sm" className="mt-4 w-full" onClick={open}>
              <Plus /> Add driver
            </Button>
          )}
        />
      ),
      trucks: (
        <ChatAddEquipments
          edit
          slim
          readonly={isReadonly}
          type="trucks"
          orgId={application.orgId}
          renderFooter={(open) => (
            <Button type="button" variant="outline" size="sm" className="mt-4 w-full" onClick={open}>
              <Plus /> Add truck
            </Button>
          )}
        />
      ),
      trailers: (
        <ChatAddEquipments
          edit
          slim
          readonly={isReadonly}
          type="trailers"
          orgId={application.orgId}
          renderFooter={(open) => (
            <Button type="button" variant="outline" size="sm" className="mt-4 w-full" onClick={open}>
              <Plus /> Add trailer
            </Button>
          )}
        />
      ),
      'claims-history': (
        <ClaimsHistorySummary orgId={application.orgId} applicationId={application.id} readonly={reviewing} />
      ),
      'my-documents': (
        <MyDocumentsSummary
          orgId={application.orgId}
          readonly={documentsReadonly}
          underwriting={underwriting}
        />
      ),
      'additional-info': (
        <AdditionalInfoSummary
          application={application}
          travelDistance={orgApplication?.travelDistance ?? null}
          travelDistanceOptions={travelDistanceOptions}
          onSuccess={onSaved}
          readonly={isReadonly}
        />
      ),
    },
  }

  // Both change what the cards say, so the board waits for them rather than rewriting itself.
  if (!boardReady) return <QuotesPageSkeleton />

  const summary = (
    <ApplicationSummarySection
      data={summaryData}
      readonly={isReadonly}
      action={
        showCartButton && !reviewing ? <NavCartButton count={cart.count} onClick={cart.open} /> : undefined
      }
      openSection={openSection}
    />
  )

  return (
    <RequoteOnSaveProvider applicationId={application.id} reviewing={reviewing}>
      <div className="pb-20" data-cascade={cascade}>
        <div data-cascade-step style={cascadeStep(0)}>
          <BlurredUntilReviewed reviewing={reviewing && !informationShown}>{summary}</BlurredUntilReviewed>
        </div>
        <BlurredUntilReviewed
          reviewing={reviewing}
          card={
            <ReviewAttestationCard
              application={application}
              onConfirmed={confirmReview}
              informationShown={informationShown}
              onShowInformation={() => setInformationShown(true)}
            />
          }
        >
          <div data-cascade-step style={cascadeStep(1)}>
            <FiltersSection
              sortBy={sortBy}
              onSortByChange={setSortBy}
              selectedRatings={selectedRatings}
              onToggleRating={(rating) => toggleIn(setSelectedRatings, rating)}
              onClearRatings={() => setSelectedRatings(new Set())}
              selectedCoiSpeeds={selectedCoiSpeeds}
              onToggleCoiSpeed={(speed) => toggleIn(setSelectedCoiSpeeds, speed)}
              onClearCoiSpeeds={() => setSelectedCoiSpeeds(new Set())}
              selectedCoverages={selectedCoverages}
              onToggleCoverage={(coverage) => toggleIn(setSelectedCoverages, coverage)}
              onClearCoverages={() => setSelectedCoverages(new Set())}
              financingAvailable={financingAvailable}
              onToggleFinancingAvailable={() => setFinancingAvailable((current) => !current)}
              onClearFinancingAvailable={() => setFinancingAvailable(false)}
              selectedClaimsCapabilities={selectedClaimsCapabilities}
              onToggleClaimsCapability={(key) => toggleIn(setSelectedClaimsCapabilities, key)}
              onClearClaimsCapabilities={() => setSelectedClaimsCapabilities(new Set())}
              onClearAllFilters={clearAllFilters}
              isUpdatingQuotes={false}
              sortDisabled={pricedQuoteCount < 2}
            />
          </div>
          <div data-cascade-step style={cascadeStep(2)}>
            <TopQuotesHeader
              onScrollPrev={() => carouselRef.current?.scrollPrev()}
              onScrollNext={() => carouselRef.current?.scrollNext()}
              canScrollPrev={edges.canScrollPrev}
              canScrollNext={edges.canScrollNext}
              policyExpirationDate={policyExpirationDate}
              notice={
                !staffView &&
                !reviewing &&
                carrierStillPricing && (
                  <NotificationsPrompt
                    dismissKey="quotes"
                    onAnswered={(permission) => track(QuotesEvent.NOTIFICATIONS_REQUESTED, { permission })}
                  >
                    Get notified when new quotes land
                  </NotificationsPrompt>
                )
              }
            />
          </div>
          <div ref={boardRef} style={{ '--cascade-cards-delay': `${CARDS_STEP * 90}ms` } as CSSProperties}>
            {/* This copy would blame the customer for a board emptied by withheld declines. */}
            {showsEmptyState ? (
              <p
                data-cascade-step
                style={cascadeStep(CARDS_STEP)}
                className="text-ink-400 py-10 text-center text-sm"
              >
                {application.hasDispatchedIndications
                  ? "We're shopping your coverage. Your quotes will show up here as they come in."
                  : 'No quotes yet. Finish your application and Cai will start collecting quotes.'}
              </p>
            ) : (
              <QuoteCardsCarousel
                revealFocusedCard
                ref={carouselRef}
                quotes={visibleQuotes}
                orderKey={orderKey}
                cartKeys={cart.keys}
                onAddCoverages={cart.add}
                onRemoveCoverages={cart.remove}
                onEdgeChange={setEdges}
                onDeleteQuote={isReadonly ? undefined : (id) => deleteQuote.mutate(id)}
                awaitingCarrierCount={application.awaitingCarrierCount}
                onReviewAttestation={setAttestationCardId}
                onUploadDocument={
                  isReadonly ? undefined : (id, documentType) => setUploadTarget({ id, documentType })
                }
                leadingCard={
                  quotePackage && (
                    <PackageCard
                      quotePackage={quotePackage}
                      cartKeys={cart.keys}
                      onAddCoverages={cart.add}
                      onRemoveCoverages={cart.remove}
                    />
                  )
                }
                trailingCard={
                  isReadonly ? undefined : (
                    <UploadQuoteCard orgId={application.orgId} onUploaded={setUploadedQuoteId} />
                  )
                }
              />
            )}
          </div>
        </BlurredUntilReviewed>
        {noCarrierPriced && !staffView && !reviewing && <AllDeclinedOverlay />}
        {quotePackage && (
          <PackageAnnouncementDialog
            quotePackage={quotePackage}
            broker={broker}
            // Opens once the page has finished cascading in, not over the top of it.
            open={announcement.isOpen && !reviewing && cascade === 'done'}
            onDismiss={announcement.dismiss}
            onCheckItOut={() => {
              announcement.dismiss()
              boardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              carouselRef.current?.scrollToStart()
            }}
          />
        )}
        {!reviewing && (
          <QuoteCartCheckout quotes={quotes} onSubmit={applySelectedIndications} notice={cartNotice} />
        )}
        <UploadCarrierDocumentDialog
          applicationId={application.id}
          indicationId={uploadTarget?.id ?? null}
          documentType={uploadTarget?.documentType ?? DocumentType.LOSS_RUN}
          carrierName={quotes.find((quote) => quote.id === uploadTarget?.id)?.carrierName}
          onClose={() => setUploadTarget(null)}
        />
        <CarrierAttestationDialog
          orgId={application.orgId}
          carrierName={quotes.find((quote) => quote.id === attestationCardId)?.carrierName}
          open={!!attestationCardId}
          onOpenChange={(open) => {
            if (!open) setAttestationCardId(null)
          }}
          onAccepted={() => {
            track(QuotesEvent.CARD_ATTESTATION_ACCEPTED, {
              carrier_name: quotes.find((quote) => quote.id === attestationCardId)?.carrierName,
            })
            onEditSuccess()
          }}
        />
      </div>
    </RequoteOnSaveProvider>
  )
}
